import { useAuth } from '@/composables/use-auth';

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (!import.meta.client) return;
  console.debug('running auth middleware');
  
  // Skip middleware for public routes
  const publicRoutes = ['/login', '/signup', '/forgot-password', '/auth-management', '/terms-of-use', '/privacy-policy', '/eula'];
  if (publicRoutes.some(route => to.path.startsWith(route))) {
    return;
  }
  
  // Handle token errors by wrapping the init in a try/catch
  const { init, user, isAuthenticated, isPatient, signout } = useAuth();
  try {
    await init();
  } catch (error) {
    console.error('Auth initialization error:', error);
    // If there's an "invalid signature" or other token error, clear the auth tokens and redirect to login
    if (error.message?.includes('invalid signature') || 
        error.message?.includes('Invalid token') ||
        error.message?.includes('jwt')) {
      console.warn('Invalid token detected, clearing auth state');
      await signout();
      // Add error parameter to help the login page show appropriate message
      return navigateTo('/login?error=invalid_token');
    }
    // For other errors, just redirect to login
    return navigateTo('/login');
  }
  
  // Handle root route
  if (to.path === '/') {
    // Check if there's a token query parameter
    if (to.query.token) {
      // Redirect to login with the token to handle authentication
      return navigateTo(`/login?token=${encodeURIComponent(to.query.token)}`);
    }
    
    try {
      if (await isAuthenticated()) {
        // If authenticated, redirect to patient dashboard
        return navigateTo('/dashboard/patient');
      } else {
        // If not authenticated, redirect to login
        return navigateTo('/login');
      }
    } catch (error) {
      console.error('Authentication check error:', error);
      return navigateTo('/login');
    }
  }
  
  // For protected routes, check authentication
  try {
    if (!(await isAuthenticated())) {
      return navigateTo('/login');
    }
  } catch (error) {
    console.error('Authentication check error:', error);
    return navigateTo('/login');
  }
  
  // Check if the current user is a patient using our helper function
  if (!isPatient()) {
    console.warn('User is not a patient, redirecting to login');
    return navigateTo('/login?error=patient_only');
  }
  
  // For dashboard paths, ensure they're patient routes
  if (to.path.startsWith('/dashboard') && !to.path.includes('/patient')) {
    // Redirect to patient dashboard
    return navigateTo('/dashboard/patient');
  }
});
